import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '@/contexts/AuthContext';
import { formatINRFull, getStatusColor, formatStatus, PROJECT_STATUSES, LEAD_CATEGORIES, formatDate } from '@/lib/utils';
import { Plus, Search, FolderKanban, Trash2 } from 'lucide-react';
import { Pagination } from '@/components/Pagination';

const PAGE_SIZE = 50;

export default function ProjectsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [clients, setClients] = useState([]);
  const [regions, setRegions] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchProjects = (p = page) => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (statusFilter) params.set('status', statusFilter);
    params.set('skip', ((p - 1) * PAGE_SIZE).toString());
    params.set('limit', PAGE_SIZE.toString());
    api.get(`/projects?${params}`).then(r => { setProjects(r.data.projects || []); setTotalCount(r.data.total_count || 0); }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchProjects(page); }, [search, statusFilter, page]);
  useEffect(() => {
    api.get('/clients').then(r => setClients(r.data.clients || []));
    api.get('/regions').then(r => setRegions(r.data.regions || []));
  }, []);

  const canCreate = ['DIRECTOR', 'SALES'].includes(user?.role);
  const canDelete = user?.role === 'DIRECTOR';

  const deleteProject = async (e, projId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try { await api.delete(`/projects/${projId}`); fetchProjects(); } catch (err) { alert('Delete failed'); }
  };

  return (
    <div data-testid="projects-page" className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 font-heading tracking-tight">Projects</h1>
          <p className="text-sm text-slate-500">{totalCount} projects</p>
        </div>
        {canCreate && (
          <button onClick={() => setShowCreate(true)} data-testid="create-project-button"
            className="bg-[#1B5486] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#144066] flex items-center gap-2">
            <Plus size={16} /> New Project
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" placeholder="Search projects..." value={search} onChange={e => setSearch(e.target.value)}
            data-testid="search-projects-input"
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-md text-sm focus:ring-2 focus:ring-[#1B5486]/20 outline-none" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="filter-project-status"
          className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none">
          <option value="">All Statuses</option>
          {PROJECT_STATUSES.map(s => <option key={s} value={s}>{formatStatus(s)}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="animate-spin w-8 h-8 border-2 border-[#1B5486] border-t-transparent rounded-full" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map(p => (
            <div key={p.id} onClick={() => navigate(`/projects/${p.id}`)} data-testid={`project-card-${p.project_code}`}
              className="bg-white rounded-lg border border-slate-200 p-5 cursor-pointer hover:-translate-y-[2px] hover:shadow-sm transition-all duration-200 relative">
              {canDelete && (
                <button onClick={(e) => deleteProject(e, p.id)} data-testid={`delete-project-${p.project_code}`}
                  className="absolute top-3 right-3 text-slate-300 hover:text-red-500 z-10"><Trash2 size={14} /></button>
              )}
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-medium text-[#1B5486]">{p.project_code}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(p.status)}`}>{formatStatus(p.status)}</span>
              </div>
              <h3 className="text-sm font-medium text-slate-900 mb-2 line-clamp-2">{p.name}</h3>
              <p className="text-xs text-slate-500 mb-3">{p.client_short_name || p.client_name} | {p.location}</p>
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold text-slate-900 font-mono">{formatINRFull(p.contract_value)}</span>
                <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-500">{formatStatus(p.category)}</span>
              </div>
              <div className="flex gap-4 mt-3 pt-3 border-t border-slate-100">
                <div className="text-xs"><span className="text-slate-400">Material:</span> <span className={`${getStatusColor(p.material_status)} px-1.5 py-0.5 rounded`}>{formatStatus(p.material_status)}</span></div>
                <div className="text-xs"><span className="text-slate-400">Install:</span> <span className={`${getStatusColor(p.installation_status)} px-1.5 py-0.5 rounded`}>{formatStatus(p.installation_status)}</span></div>
              </div>
            </div>
          ))}
          {projects.length === 0 && <div className="col-span-full text-center py-12 text-slate-400">No projects found</div>}
          <div className="col-span-full"><Pagination currentPage={page} totalCount={totalCount} pageSize={PAGE_SIZE} onPageChange={setPage} /></div>
        </div>
      )}

      {showCreate && <CreateProjectModal clients={clients} regions={regions} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchProjects(); }} />}
    </div>
  );
}

function CreateProjectModal({ clients, regions, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', client_id: '', region_id: '', location: '', category: 'CCTV',
    contract_value: '', po_number: '', start_date: '', expected_end_date: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { contract_value, ...submitData } = form;
      const res = await api.post('/projects', submitData);
      // A2/E7: Show BOQ warning after project creation
      if (window.confirm('Project created! Would you like to add BOQ items now? (You can add them later from the project detail page)')) {
        window.location.href = `/projects/${res.data?.project?.id || ''}`;
      } else {
        onCreated();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : 'Failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg border w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="create-project-modal">
        <h2 className="text-lg font-semibold text-slate-900 font-heading mb-4">New Project</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><label className="block text-sm font-medium text-slate-700 mb-1">Project Name *</label>
            <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="input-field w-full" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Client *</label>
              <select value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})} required className="input-field w-full">
                <option value="">Select</option>{clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Region *</label>
              <select value={form.region_id} onChange={e => setForm({...form, region_id: e.target.value})} required className="input-field w-full">
                <option value="">Select</option>{regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}</select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
              <input value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="input-field w-full" /></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="input-field w-full">
                {LEAD_CATEGORIES.map(c => <option key={c} value={c}>{formatStatus(c)}</option>)}</select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-slate-700 mb-1">PO Number</label>
              <input value={form.po_number} onChange={e => setForm({...form, po_number: e.target.value})} className="input-field w-full" /></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Contract Value</label>
              <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-400 italic">Auto-calculated from BOQ</div></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
              <input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="input-field w-full" /></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Expected End</label>
              <input type="date" value={form.expected_end_date} onChange={e => setForm({...form, expected_end_date: e.target.value})} className="input-field w-full" /></div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-slate-200 py-2 rounded-md text-sm hover:bg-slate-50">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 bg-[#1B5486] text-white py-2 rounded-md text-sm font-medium hover:bg-[#144066] disabled:opacity-60">
              {saving ? 'Creating...' : 'Create Project'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
